import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PlayersAPI } from '../api';
import { useToast } from '../hooks/use-toast';

const AGE_GROUPS = ['U8', 'U10', 'U12', 'U14', 'U16', 'U19', 'U22', 'College', 'Pro'];
const POSITIONS = ['Forward', 'Defense', 'Goalie'];

const emptyForm = {
  firstName: '',
  lastName: '',
  team: '',
  jerseyNumber: '',
  position: '',
  shot: '',       // 'L' or 'R' in UI, mapped to 'Left'/'Right'
  ageGroup: '',
};

export default function AddPlayerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.firstName || !form.lastName) {
      toast({ title: 'Name required', description: 'Enter first and last name.' });
      return;
    }
    if (!form.team) return toast({ title: 'Team required' });
    if (!form.position) return toast({ title: 'Position required' });
    if (!form.shot) return toast({ title: 'Shot required' });
    if (!form.ageGroup) return toast({ title: 'Age group required' });

    setSaving(true);
    try {
      const created = await PlayersAPI.create({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        team: form.team.trim(),
        jerseyNumber: parseInt(form.jerseyNumber || '0', 10) || 0,
        position: form.position,
        shoots: form.shot === 'L' ? 'Left' : 'Right',
        ageGroup: form.ageGroup,
      });
      toast({ title: 'Player added', description: `${created.firstName} ${created.lastName}` });
      setForm(emptyForm);
      onCreated && onCreated(created);
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Could not add player', description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="add-player-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 font-display">Add Player</DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Enter the athlete's details. All fields are required except jersey number.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-600 text-xs">First Name</Label>
            <Input data-testid="add-player-first-name" value={form.firstName}
              onChange={e => set('firstName', e.target.value)}
              className="bg-white border-slate-200 text-slate-900" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Last Name</Label>
            <Input data-testid="add-player-last-name" value={form.lastName}
              onChange={e => set('lastName', e.target.value)}
              className="bg-white border-slate-200 text-slate-900" />
          </div>

          <div className="col-span-2">
            <Label className="text-slate-600 text-xs">Team</Label>
            <Input data-testid="add-player-team" value={form.team}
              onChange={e => set('team', e.target.value)}
              placeholder="e.g. Frost Wolves U18"
              className="bg-white border-slate-200 text-slate-900" />
          </div>

          <div>
            <Label className="text-slate-600 text-xs">Jersey #</Label>
            <Input data-testid="add-player-jersey" type="number" value={form.jerseyNumber}
              onChange={e => set('jerseyNumber', e.target.value)}
              className="bg-white border-slate-200 text-slate-900" />
          </div>

          <div>
            <Label className="text-slate-600 text-xs">Position</Label>
            <Select value={form.position} onValueChange={v => set('position', v)}>
              <SelectTrigger data-testid="add-player-position" className="bg-white border-slate-200 text-slate-900">
                <SelectValue placeholder="Select position" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-600 text-xs">Shot</Label>
            <Select value={form.shot} onValueChange={v => set('shot', v)}>
              <SelectTrigger data-testid="add-player-shot" className="bg-white border-slate-200 text-slate-900">
                <SelectValue placeholder="Select shot" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="L">L (Left)</SelectItem>
                <SelectItem value="R">R (Right)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-600 text-xs">Age Group</Label>
            <Select value={form.ageGroup} onValueChange={v => set('ageGroup', v)}>
              <SelectTrigger data-testid="add-player-age" className="bg-white border-slate-200 text-slate-900">
                <SelectValue placeholder="Select age" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {AGE_GROUPS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}
            data-testid="add-player-cancel"
            className="text-slate-600 hover:bg-slate-100">Cancel</Button>
          <Button onClick={submit} disabled={saving}
            data-testid="add-player-submit"
            className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
            {saving ? 'Saving' : 'Add Player'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
